import { useEffect, useState } from "react";
import { Plus, PackagePlus, PackageMinus, Pencil, Loader2, X, History, Upload, Video, Share2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fcfa, formatDate, PLACEHOLDER_IMG } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const inputCls = "border border-brand-line rounded-xl px-4 py-2.5 w-full focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none bg-white text-brand-ink";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-xl text-brand-ink">{title}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-brand-cream rounded-full"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Products() {
  const { user } = useAuth();
  const canManage = user?.role === "patron" || user?.role === "associe";
  const [products, setProducts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [restocking, setRestocking] = useState(null);
  const [destocking, setDestocking] = useState(null);
  const [showMoves, setShowMoves] = useState(false);
  const [moves, setMoves] = useState([]);

  const load = () => api.get("/admin/products").then(({ data }) => setProducts(data));
  useEffect(() => { load(); }, []);

  const openMoves = async () => {
    const { data } = await api.get("/admin/stock-movements");
    setMoves(data);
    setShowMoves(true);
  };

  const copyLinks = () => {
    const origin = window.location.origin;
    const avail = (products || []).filter((p) => !p.coming_soon);
    const lines = avail.map((p) => `• ${p.name} — ${fcfa(p.price)}\n👉 ${origin}/produit/${p.id}`);
    const msg = `Bienvenue chez Noumabou 🌿\nCliquez sur le produit qui vous intéresse pour commander :\n\n${lines.join("\n\n")}\n\nMerci et à bientôt !`;
    navigator.clipboard.writeText(msg);
    toast.success("Message + liens copiés ! Collez-le dans WhatsApp Business.");
  };

  if (products === null) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink">Stock & produits</h1>
          <p className="text-brand-muted mt-1">{products.length} produit(s)</p>
        </div>
        <div className="flex gap-2">
          <button onClick={copyLinks} data-testid="copy-links-button" className="bg-[#25D366] text-white hover:brightness-95 rounded-full px-4 py-2.5 font-semibold flex items-center gap-2 text-sm">
            <Share2 className="w-4 h-4" /> Copier les liens (WhatsApp)
          </button>
          <button onClick={openMoves} data-testid="movements-button" className="bg-white border border-brand-line hover:bg-brand-cream rounded-full px-4 py-2.5 font-semibold text-brand-ink flex items-center gap-2 text-sm">
            <History className="w-4 h-4" /> Mouvements
          </button>
          {canManage && (
            <button onClick={() => setEditing({})} data-testid="add-product-button" className="bg-brand text-white hover:bg-brand-hover rounded-full px-4 py-2.5 font-semibold flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" /> Nouveau produit
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-brand-line rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-cream text-brand-sub text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Produit</th>
                <th className="px-4 py-3 font-semibold">Prix vente</th>
                {canManage && <th className="px-4 py-3 font-semibold">Prix achat</th>}
                {canManage && <th className="px-4 py-3 font-semibold">Marge</th>}
                <th className="px-4 py-3 font-semibold">Stock</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-line">
              {products.map((p) => (
                <tr key={p.id} data-testid={`product-row-${p.id}`} className="hover:bg-brand-cream/40">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={p.image_url || PLACEHOLDER_IMG} alt="" className="w-10 h-10 rounded-lg object-cover bg-brand-cream" />
                      <div>
                        <p className="font-medium text-brand-ink">{p.name}</p>
                        {!p.active && <span className="text-xs text-brand-muted">Inactif</span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold text-brand-ink">{fcfa(p.price)}</td>
                  {canManage && <td className="px-4 py-3 text-brand-sub">{fcfa(p.cost_price)}</td>}
                  {canManage && <td className="px-4 py-3 text-emerald-700 font-medium">{fcfa(p.margin)}</td>}
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${p.low_stock ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                      {p.stock_qty} unité(s)
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      {canManage && (
                        <>
                          <button onClick={() => setRestocking(p)} data-testid={`restock-${p.id}`} title="Entrée de stock" className="p-2 hover:bg-brand-cream rounded-lg text-brand-accent"><PackagePlus className="w-4 h-4" /></button>
                          <button onClick={() => setDestocking(p)} data-testid={`destock-${p.id}`} title="Sortie de stock" className="p-2 hover:bg-brand-cream rounded-lg text-brand-secondary"><PackageMinus className="w-4 h-4" /></button>
                          <button onClick={() => setEditing(p)} data-testid={`edit-${p.id}`} title="Modifier" className="p-2 hover:bg-brand-cream rounded-lg text-brand-sub"><Pencil className="w-4 h-4" /></button>
                        </>
                      )}
                      {!canManage && <span className="text-xs text-brand-muted">—</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <ProductModal product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {restocking && <RestockModal product={restocking} onClose={() => setRestocking(null)} onSaved={() => { setRestocking(null); load(); }} />}
      {destocking && <DestockModal product={destocking} onClose={() => setDestocking(null)} onSaved={() => { setDestocking(null); load(); }} />}
      {showMoves && (
        <Modal title="Historique des mouvements" onClose={() => setShowMoves(false)}>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {moves.length === 0 ? <p className="text-sm text-brand-muted">Aucun mouvement.</p> : moves.map((m) => (
              <div key={m.id} className="flex items-center justify-between text-sm border-b border-brand-line pb-2">
                <div>
                  <p className="font-medium text-brand-ink">{m.product_name}</p>
                  <p className="text-xs text-brand-muted">{m.reason} · {formatDate(m.created_at)}</p>
                </div>
                <span className={`font-semibold ${m.qty_change >= 0 ? "text-emerald-700" : "text-brand-secondary"}`}>
                  {m.qty_change >= 0 ? "+" : ""}{m.qty_change} → {m.qty_after}
                </span>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

function ProductModal({ product, onClose, onSaved }) {
  const isNew = !product.id;
  const [form, setForm] = useState({
    name: product.name || "", description: product.description || "",
    usage: product.usage || "", benefits: product.benefits || "",
    image_url: product.image_url || "",
    video_url: product.video_url || "", video_path: product.video_path || "",
    price: product.price || "", cost_price: product.cost_price || "", stock_qty: product.stock_qty || 0,
    low_stock_threshold: product.low_stock_threshold ?? 5, active: product.active ?? true,
    coming_soon: product.coming_soon ?? false,
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState("");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (file, kind) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/product-media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.kind === "image") setForm((f) => ({ ...f, image_url: data.url }));
      else setForm((f) => ({ ...f, video_path: data.url, video_url: "" }));
      toast.success("Fichier téléversé");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'upload");
    } finally { setUploading(""); }
  };

  const save = async () => {
    setError(""); setLoading(true);
    try {
      const payload = {
        ...form, price: Number(form.price), cost_price: Number(form.cost_price),
        stock_qty: Number(form.stock_qty), low_stock_threshold: Number(form.low_stock_threshold),
      };
      if (isNew) await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${product.id}`, {
        name: payload.name, description: payload.description, usage: payload.usage, benefits: payload.benefits,
        image_url: payload.image_url, video_url: payload.video_url, video_path: payload.video_path,
        price: payload.price, cost_price: payload.cost_price, low_stock_threshold: payload.low_stock_threshold,
        active: form.active, coming_soon: form.coming_soon,
      });
      toast.success(isNew ? "Produit créé" : "Produit mis à jour");
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <Modal title={isNew ? "Nouveau produit" : "Fiche produit"} onClose={onClose}>
      <div className="space-y-3">
        <input data-testid="product-name-input" className={inputCls} placeholder="Nom du produit" value={form.name} onChange={set("name")} />

        {/* Image */}
        <div>
          <label className="text-xs font-semibold text-brand-sub">Photo du produit</label>
          <div className="flex items-center gap-3 mt-1">
            <div className="w-16 h-16 rounded-xl overflow-hidden bg-brand-cream border border-brand-line flex items-center justify-center shrink-0">
              <img src={form.image_url || PLACEHOLDER_IMG} alt="" className="w-full h-full object-contain" />
            </div>
            <label className="cursor-pointer bg-white border border-brand-line hover:bg-brand-cream rounded-full px-4 py-2 text-sm font-semibold text-brand-ink flex items-center gap-2">
              {uploading === "image" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Téléverser une image
              <input type="file" accept="image/*" className="hidden" data-testid="product-image-upload" onChange={(e) => upload(e.target.files[0], "image")} />
            </label>
          </div>
          <input className={`${inputCls} mt-2`} placeholder="…ou coller une URL d'image" value={form.image_url} onChange={set("image_url")} />
        </div>

        {/* Vidéo */}
        <div>
          <label className="text-xs font-semibold text-brand-sub">Vidéo du produit (produit en main)</label>
          <div className="flex items-center gap-3 mt-1">
            <label className="cursor-pointer bg-white border border-brand-line hover:bg-brand-cream rounded-full px-4 py-2 text-sm font-semibold text-brand-ink flex items-center gap-2">
              {uploading === "video" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Téléverser une vidéo (.mp4, max 45 Mo)
              <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" data-testid="product-video-upload" onChange={(e) => upload(e.target.files[0], "video")} />
            </label>
            {form.video_path && <span className="text-xs text-emerald-700 font-semibold flex items-center gap-1"><Video className="w-4 h-4" /> Vidéo ajoutée</span>}
          </div>
          <input className={`${inputCls} mt-2`} placeholder="…ou coller un lien YouTube / TikTok" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value, video_path: "" })} data-testid="product-video-input" />
        </div>

        <textarea className={inputCls} placeholder="Présentation / description du produit" rows={2} value={form.description} onChange={set("description")} data-testid="product-description-input" />
        <textarea className={inputCls} placeholder="Mode d'emploi / posologie (comment prendre le produit)" rows={2} value={form.usage} onChange={set("usage")} data-testid="product-usage-input" />
        <textarea className={inputCls} placeholder="Bienfaits (ce que le produit fait)" rows={2} value={form.benefits} onChange={set("benefits")} data-testid="product-benefits-input" />

        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-brand-muted">Prix de vente (FCFA)</label><input data-testid="product-price-input" type="number" className={inputCls} value={form.price} onChange={set("price")} /></div>
          <div><label className="text-xs text-brand-muted">Prix d'achat (FCFA)</label><input type="number" className={inputCls} value={form.cost_price} onChange={set("cost_price")} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {isNew && <div><label className="text-xs text-brand-muted">Stock initial</label><input type="number" className={inputCls} value={form.stock_qty} onChange={set("stock_qty")} /></div>}
          <div><label className="text-xs text-brand-muted">Seuil stock bas</label><input type="number" className={inputCls} value={form.low_stock_threshold} onChange={set("low_stock_threshold")} /></div>
        </div>
        {!isNew && (
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-brand-sub"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Actif (visible en boutique)</label>
            <label className="flex items-center gap-2 text-sm text-brand-sub"><input data-testid="product-coming-soon" type="checkbox" checked={form.coming_soon} onChange={(e) => setForm({ ...form, coming_soon: e.target.checked })} /> Bientôt disponible (masque le prix, désactive la commande)</label>
          </div>
        )}
        {error && <p className="text-sm text-brand-secondary">{error}</p>}
        <button onClick={save} disabled={loading} data-testid="save-product-button" className="w-full bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enregistrer"}
        </button>
      </div>
    </Modal>
  );
}

function RestockModal({ product, onClose, onSaved }) {
  const [qty, setQty] = useState(10);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/products/${product.id}/restock`, { quantity: Number(qty), note });
      toast.success("Stock réapprovisionné");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Modal title={`Réapprovisionner — ${product.name}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-brand-muted">Stock actuel : <span className="font-semibold text-brand-ink">{product.stock_qty}</span></p>
        <div><label className="text-xs text-brand-muted">Quantité à ajouter</label><input data-testid="restock-qty-input" type="number" className={inputCls} value={qty} onChange={(e) => setQty(e.target.value)} /></div>
        <div><label className="text-xs text-brand-muted">Note (optionnel)</label><input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex: Livraison fournisseur" /></div>
        <button onClick={save} disabled={loading} data-testid="save-restock-button" className="w-full bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Ajouter au stock"}
        </button>
      </div>
    </Modal>
  );
}

function DestockModal({ product, onClose, onSaved }) {
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/products/${product.id}/destock`, { quantity: Number(qty), note });
      toast.success("Sortie de stock enregistrée");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Modal title={`Sortie de stock — ${product.name}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-brand-muted">Stock actuel : <span className="font-semibold text-brand-ink">{product.stock_qty}</span></p>
        <div><label className="text-xs text-brand-muted">Quantité à retirer</label><input data-testid="destock-qty-input" type="number" min={1} max={product.stock_qty} className={inputCls} value={qty} onChange={(e) => setQty(e.target.value)} /></div>
        <div><label className="text-xs text-brand-muted">Motif (optionnel)</label><input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex: Produit abîmé, périmé, don, correction" /></div>
        <button onClick={save} disabled={loading} data-testid="save-destock-button" className="w-full bg-brand-secondary text-white hover:brightness-95 rounded-full px-6 py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Retirer du stock"}
        </button>
      </div>
    </Modal>
  );
}
